'use client';

import React, {
    useState,
    createContext,
    useContext,
    Dispatch,
    SetStateAction,
    useMemo,
} from 'react';
import gsap from 'gsap';
import { CSSRulePlugin } from 'gsap/CSSRulePlugin';
import { Flip } from 'gsap/Flip';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import useIsomorphicLayoutEffect from '@/lib/hooks/isomorphic-layout-effect';

// 定义上下文类型
interface TransitionContextType {
    timeline: gsap.core.Timeline;
    setTimeline: Dispatch<SetStateAction<gsap.core.Timeline>>;
    resetTimeline: () => void;
    disableScroll: () => void;
    enableScroll: () => void;
}

const defaultTimeline = gsap.timeline({ paused: true });

const TransitionContext = createContext<TransitionContextType>({
    timeline: defaultTimeline,
    setTimeline: () => undefined,
    resetTimeline: () => {},
    disableScroll: () => {},
    enableScroll: () => {},
});

export function TransitionContextProvider({
    children,
}: Readonly<{
    children: React.ReactNode;
}>) {
    const [timeline, setTimeline] = useState(defaultTimeline);

    useIsomorphicLayoutEffect(() => {
        gsap.registerPlugin(ScrollTrigger);
        gsap.registerPlugin(useGSAP);
        gsap.registerPlugin(CSSRulePlugin);
        gsap.registerPlugin(Flip);
        gsap.config({ force3D: true });
    }, []);

    const resetTimeline = () => {
        timeline.pause().clear();
    };

    const disableScroll = () => {
        document.body.style.overflow = 'hidden';
    };

    const enableScroll = () => {
        document.body.style.overflow = 'auto';
    };

    const value = useMemo(
        () => ({
            timeline,
            setTimeline,
            resetTimeline,
            disableScroll,
            enableScroll,
        }),
        [timeline, setTimeline, resetTimeline, disableScroll, enableScroll],
    );

    return (
        <TransitionContext.Provider value={value}>
            {children}
        </TransitionContext.Provider>
    );
}

export default function useTransitionContext() {
    return useContext(TransitionContext);
}
